import React, {ReactElement} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {grid} from "@pg-design/grid";
import {borderRadius, calculateRemSize, flex, p} from "@pg-design/helpers-css";
import {BrandRPLogoIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

interface IOffsetType {
    bottom?: string;
}

export interface IFeedbackPopupTriggerProps {
    onOpen: () => void;
    position: "left" | "right";
    zIndex: number;
    isPromptActive: boolean;
    desktopDataTestId?: string;
    mobileDataTestId?: string;
    mobile?: ITriggerConfig & {
        text?: string;
        triggerPromptInset?: string;
    };
    desktop?: ITriggerConfig & {
        transparentBg?: boolean;
        fixedWidth?: string;
        fixedHeight?: string;
        noPadding?: boolean;
        stickToContent?: boolean;
        triggerInset?: string;
        triggerPromptInset?: string;
    };
}

interface ITriggerConfig {
    hideTrigger?: boolean;
    offset?: IOffsetType;
    prompt?: string;
    slot?: ReactElement;
}

export const FeedbackPopupTrigger = (props: IFeedbackPopupTriggerProps) => {
    const theme = useTheme();

    if (props.mobile?.hideTrigger && props.desktop?.hideTrigger) {
        return null;
    }

    return (
        <>
            {props.mobile?.hideTrigger ? null : (
                <>
                    <MobileTrigger
                        onClick={props.onOpen}
                        position={props.position}
                        zIndex={props.zIndex}
                        offset={props.mobile?.offset}
                        data-testid={props.mobileDataTestId}
                    >
                        {props.mobile?.slot ?? <BrandRPLogoIcon size="2.4" wrapperSize="4.8" wrapperColor={theme.colors.primary} />}
                        {props.mobile?.prompt && props.isPromptActive ? (
                            <MobileTriggerPrompt inset={props.mobile.triggerPromptInset}>{props.mobile?.prompt}</MobileTriggerPrompt>
                        ) : null}
                    </MobileTrigger>
                    {props.mobile?.text && (
                        <MobileTriggerText onClick={props.onOpen} position={props.position} zIndex={props.zIndex} offset={props.mobile?.offset}>
                            <Text variant="headline_5">{props.mobile.text}</Text>
                        </MobileTriggerText>
                    )}
                </>
            )}

            {props.desktop?.hideTrigger ? null : (
                <DesktopTrigger
                    onClick={props.onOpen}
                    position={props.position}
                    zIndex={props.zIndex}
                    offset={props.desktop?.offset}
                    transparent={props.desktop?.transparentBg}
                    width={props.desktop?.fixedWidth}
                    height={props.desktop?.fixedHeight}
                    stickToConent={props.desktop?.stickToContent}
                    inset={props.desktop?.triggerInset}
                    data-testid={props.desktopDataTestId}
                >
                    {props.desktop?.slot ?? <BrandRPLogoIcon size="1.8" wrapperSize="3.6" fill={theme.colors.primary} wrapperColor={theme.colors.secondary} />}
                    {props.desktop?.prompt && props.isPromptActive ? (
                        <DesktopTriggerPrompt position={props.position} inset={props.desktop.triggerPromptInset}>
                            {props.desktop?.prompt}
                        </DesktopTriggerPrompt>
                    ) : null}
                </DesktopTrigger>
            )}
        </>
    );
};

type ITriggerProps = {
    offset?: IOffsetType;
} & Pick<IFeedbackPopupTriggerProps, "position" | "zIndex">;

const MobileTrigger = styled.div<ITriggerProps>`
    min-width: 4.8rem;
    min-height: 4.8rem;
    position: fixed;
    background-color: #fff;
    border-radius: 50%;
    cursor: pointer;
    user-select: none;
    box-shadow:
        0 1.4rem 2.8rem rgba(0, 0, 0, 0.25),
        0 1rem 1rem rgba(0, 0, 0, 0.22);
    ${flex("center", "center")};
    bottom: ${({offset}) => offset?.bottom ?? calculateRemSize(10)};
    z-index: ${({zIndex}) => zIndex};

    ${(props) =>
        props.position === "right"
            ? css`
                  right: 1.6rem;
              `
            : css`
                  left: 1.6rem;
              `};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        display: none;
    }
`;

const MobileTriggerPrompt = styled.span<{inset?: string}>`
    ${({theme, inset}) => css`
        position: absolute;
        top: -0.5rem;
        right: -0.5rem;
        min-width: 2rem;
        height: 2rem;
        border-radius: 1rem;
        background-color: ${theme.colors.danger};
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        ${p(0, 1)};

        ${inset ? `inset: ${inset};` : ""};
    `}
`;

const MobileTriggerText = styled.div<ITriggerProps>`
    ${({position, offset, zIndex, theme}) => css`
        position: fixed;
        cursor: pointer;
        bottom: ${offset?.bottom || calculateRemSize(10)};
        z-index: ${zIndex};
        min-height: 4.6rem;
        display: flex;
        align-items: center;
        ${position === "right"
            ? css`
                  right: 7.5rem;
              `
            : css`
                  left: 7.5rem;
              `};

        @media (min-width: ${theme.breakpoints.md}) {
            display: none;
        }
    `}
`;

const DESKTOP_TRIGGER_HEIGHT_IN_REM = 5;

const DesktopTrigger = styled.div<
    ITriggerProps & {transparent?: boolean; width?: string; height?: string; noPadding?: boolean; stickToConent?: boolean; inset?: string}
>`
    position: fixed;
    height: ${DESKTOP_TRIGGER_HEIGHT_IN_REM}rem;
    bottom: ${({offset}) => offset?.bottom ?? calculateRemSize(10)};
    background-color: ${({theme, transparent}) => (transparent ? "transparent" : theme.colors.secondary)};
    cursor: pointer;
    user-select: none;
    ${flex("center", "center")};
    z-index: ${({zIndex}) => zIndex};
    color: #fff;
    ${p(0, 2)};
    ${borderRadius(0, 0, 1, 1)};

    ${(props) =>
        props.position === "right"
            ? css`
                  right: 0;
                  transform-origin: right;
                  transform: rotate(90deg) translate(${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, 50%);
              `
            : css`
                  left: 0;
                  transform-origin: left;
                  transform: rotate(-90deg) translate(-${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, 50%);
              `};

    ${({position, width, height, noPadding, stickToConent, inset}) => css`
        ${width ? `width: ${width};` : ""};
        ${height ? `height: ${height};` : ""};
        ${noPadding ? "padding: 0;" : ""};
        ${inset ? `inset: ${inset};` : ""};

        ${stickToConent
            ? css`
                  @media screen and (min-width: calc(${grid.lg.width}px${width ? ` + calc(${width} * 2)` : ""})) {
                      left: 50%;
                      transform: ${position === "left"
                          ? `rotate(-90deg) translate(-${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, calc(calc(${grid.lg.width}px + calc(${width} * 2)) / 2 * -1))`
                          : `rotate(-90deg) translate(${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, calc(calc(${grid.lg.width}px + calc(${width} * 2)) / 2))
                      `};
                  }
              `
            : ""}
    `};

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        display: none;
    }
`;

const DesktopTriggerPrompt = styled.span<Pick<IFeedbackPopupTriggerProps, "position"> & {inset?: string}>`
    position: absolute;
    bottom: -0.5rem;
    min-width: 2rem;
    height: 2rem;
    border-radius: 1rem;
    background-color: ${({theme}) => theme.colors.danger};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    ${p(0, 1)};
    ${({inset}) => (inset ? `inset: ${inset};` : "")};
    
    ${({position}) =>
        position === "right"
            ? css`
                  left: -0.5rem;
                  transform: rotate(-90deg);
              `
            : css`
                  right: -0.5rem;
                  transform: rotate(90deg);
              `}}
`;
import React, {ReactElement} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {grid} from "@pg-design/grid";
import {borderRadius, calculateRemSize, flex, p} from "@pg-design/helpers-css";
import {BrandRPLogoIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

interface IOffsetType {
    bottom?: string;
}

export interface IFeedbackPopupTriggerProps {
    onOpen: () => void;
    position: "left" | "right";
    zIndex: number;
    isPromptActive: boolean;
    desktopDataTestId?: string;
    mobileDataTestId?: string;
    mobile?: ITriggerConfig & {
        text?: string;
        triggerPromptInset?: string;
    };
    desktop?: ITriggerConfig & {
        transparentBg?: boolean;
        fixedWidth?: string;
        fixedHeight?: string;
        noPadding?: boolean;
        stickToContent?: boolean;
        triggerInset?: string;
        triggerPromptInset?: string;
    };
}

interface ITriggerConfig {
    hideTrigger?: boolean;
    offset?: IOffsetType;
    prompt?: string;
    slot?: ReactElement;
}

export const FeedbackPopupTrigger = (props: IFeedbackPopupTriggerProps) => {
    const theme = useTheme();

    if (props.mobile?.hideTrigger && props.desktop?.hideTrigger) {
        return null;
    }

    return (
        <>
            {props.mobile?.hideTrigger ? null : (
                <>
                    <MobileTrigger
                        onClick={props.onOpen}
                        position={props.position}
                        zIndex={props.zIndex}
                        offset={props.mobile?.offset}
                        data-testid={props.mobileDataTestId}
                    >
                        {props.mobile?.slot ?? <BrandRPLogoIcon size="2.4" wrapperSize="4.8" wrapperColor={theme.colors.primary} />}
                        {props.mobile?.prompt && props.isPromptActive ? (
                            <MobileTriggerPrompt inset={props.mobile.triggerPromptInset}>{props.mobile?.prompt}</MobileTriggerPrompt>
                        ) : null}
                    </MobileTrigger>
                    {props.mobile?.text && (
                        <MobileTriggerText onClick={props.onOpen} position={props.position} zIndex={props.zIndex} offset={props.mobile?.offset}>
                            <Text variant="headline_5">{props.mobile.text}</Text>
                        </MobileTriggerText>
                    )}
                </>
            )}

            {props.desktop?.hideTrigger ? null : (
                <DesktopTrigger
                    onClick={props.onOpen}
                    position={props.position}
                    zIndex={props.zIndex}
                    offset={props.desktop?.offset}
                    transparent={props.desktop?.transparentBg}
                    width={props.desktop?.fixedWidth}
                    height={props.desktop?.fixedHeight}
                    stickToConent={props.desktop?.stickToContent}
                    inset={props.desktop?.triggerInset}
                    data-testid={props.desktopDataTestId}
                >
                    {props.desktop?.slot ?? <BrandRPLogoIcon size="1.8" wrapperSize="3.6" fill={theme.colors.primary} wrapperColor={theme.colors.secondary} />}
                    {props.desktop?.prompt && props.isPromptActive ? (
                        <DesktopTriggerPrompt position={props.position} inset={props.desktop.triggerPromptInset}>
                            {props.desktop?.prompt}
                        </DesktopTriggerPrompt>
                    ) : null}
                </DesktopTrigger>
            )}
        </>
    );
};

type ITriggerProps = {
    offset?: IOffsetType;
} & Pick<IFeedbackPopupTriggerProps, "position" | "zIndex">;

const MobileTrigger = styled.div<ITriggerProps>`
    min-width: 4.8rem;
    min-height: 4.8rem;
    position: fixed;
    background-color: #fff;
    border-radius: 50%;
    cursor: pointer;
    user-select: none;
    box-shadow:
        0 1.4rem 2.8rem rgba(0, 0, 0, 0.25),
        0 1rem 1rem rgba(0, 0, 0, 0.22);
    ${flex("center", "center")};
    bottom: ${({offset}) => offset?.bottom ?? calculateRemSize(10)};
    z-index: ${({zIndex}) => zIndex};

    ${(props) =>
        props.position === "right"
            ? css`
                  right: 1.6rem;
              `
            : css`
                  left: 1.6rem;
              `};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        display: none;
    }
`;

const MobileTriggerPrompt = styled.span<{inset?: string}>`
    ${({theme, inset}) => css`
        position: absolute;
        top: -0.5rem;
        right: -0.5rem;
        min-width: 2rem;
        height: 2rem;
        border-radius: 1rem;
        background-color: ${theme.colors.danger};
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        ${p(0, 1)};

        ${inset ? `inset: ${inset};` : ""};
    `}
`;

const MobileTriggerText = styled.div<ITriggerProps>`
    ${({position, offset, zIndex, theme}) => css`
        position: fixed;
        cursor: pointer;
        bottom: ${offset?.bottom || calculateRemSize(10)};
        z-index: ${zIndex};
        min-height: 4.6rem;
        display: flex;
        align-items: center;
        ${position === "right"
            ? css`
                  right: 7.5rem;
              `
            : css`
                  left: 7.5rem;
              `};

        @media (min-width: ${theme.breakpoints.md}) {
            display: none;
        }
    `}
`;

const DESKTOP_TRIGGER_HEIGHT_IN_REM = 5;

const DesktopTrigger = styled.div<
    ITriggerProps & {transparent?: boolean; width?: string; height?: string; noPadding?: boolean; stickToConent?: boolean; inset?: string}
>`
    position: fixed;
    height: ${DESKTOP_TRIGGER_HEIGHT_IN_REM}rem;
    bottom: ${({offset}) => offset?.bottom ?? calculateRemSize(10)};
    background-color: ${({theme, transparent}) => (transparent ? "transparent" : theme.colors.secondary)};
    cursor: pointer;
    user-select: none;
    ${flex("center", "center")};
    z-index: ${({zIndex}) => zIndex};
    color: #fff;
    ${p(0, 2)};
    ${borderRadius(0, 0, 1, 1)};

    ${(props) =>
        props.position === "right"
            ? css`
                  right: 0;
                  transform-origin: right;
                  transform: rotate(90deg) translate(${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, 50%);
              `
            : css`
                  left: 0;
                  transform-origin: left;
                  transform: rotate(-90deg) translate(-${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, 50%);
              `};

    ${({position, width, height, noPadding, stickToConent, inset}) => css`
        ${width ? `width: ${width};` : ""};
        ${height ? `height: ${height};` : ""};
        ${noPadding ? "padding: 0;" : ""};
        ${inset ? `inset: ${inset};` : ""};

        ${stickToConent
            ? css`
                  @media screen and (min-width: calc(${grid.lg.width}px${width ? ` + calc(${width} * 2)` : ""})) {
                      left: 50%;
                      transform: ${position === "left"
                          ? `rotate(-90deg) translate(-${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, calc(calc(${grid.lg.width}px + calc(${width} * 2)) / 2 * -1))`
                          : `rotate(-90deg) translate(${DESKTOP_TRIGGER_HEIGHT_IN_REM / 2}rem, calc(calc(${grid.lg.width}px + calc(${width} * 2)) / 2))
                      `};
                  }
              `
            : ""}
    `};

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        display: none;
    }
`;

const DesktopTriggerPrompt = styled.span<Pick<IFeedbackPopupTriggerProps, "position"> & {inset?: string}>`
    position: absolute;
    bottom: -0.5rem;
    min-width: 2rem;
    height: 2rem;
    border-radius: 1rem;
    background-color: ${({theme}) => theme.colors.danger};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    ${p(0, 1)};
    ${({inset}) => (inset ? `inset: ${inset};` : "")};
    
    ${({position}) =>
        position === "right"
            ? css`
                  left: -0.5rem;
                  transform: rotate(-90deg);
              `
            : css`
                  right: -0.5rem;
                  transform: rotate(90deg);
              `}}
`;
